import { createReducer } from "typesafe-actions";

import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Activity } from "@mapmycustomers/shared/types/entity";
import { OutOfCadenceEntity } from "@mapmycustomers/shared/types/entity/activities/OutOfCadenceEntity";
import ActivitySuggestion from "@mapmycustomers/shared/types/entity/ActivitySuggestion";
import { MapEntry } from "@mapmycustomers/shared/types/map";

import { CreateEditActivityModalActions } from "@app/component/createEditEntity/Activity/store";
import { updateActivity } from "@app/component/createEditEntity/Activity/store/actions";
import CalendarViewState from "@app/component/view/CalendarView/types/CalendarViewState";
import localSettings from "@app/config/LocalSettings";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import { Actions as AppActions, initializeApp, reinitializeApp } from "@app/store/app/actions";
import { getWeekStart } from "@app/util/dates";
import activityFieldModel, { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import {
  createAndCondition,
  createSimpleCondition,
  getSimpleConditionForField,
  upsertSimpleConditionsWithinTag,
} from "@app/util/filters/utils";
import { preprocessViewStateForActivities } from "@app/util/viewModel/preprocessViewStateForActivities";

import MapEntityWithActivity from "../type/MapEntityWithActivity";

import {
  Actions,
  applyCalendarViewSettings,
  fetchCalendarActivities,
  fetchNearbyEntities,
  fetchOutOfCadenceEntities,
  findActivities,
  initializeCalendarView,
  moveResizeActivity,
  postponeActivity,
  updateActivityInList,
} from "./actions";
import { CALENDAR_VIEW_STATE } from "./saga";

const DEFAULT_CALENDAR_VIEW_STATE: CalendarViewState = {
  columns: [],
  filter: createAndCondition([
    createAndCondition(
      [
        createSimpleCondition(
          ActivityFieldName.START_AT,
          FilterOperator.ON,
          getWeekStart(Date.now())
        ),
      ],
      EntityType.ACTIVITY
    ),
  ]),
  range: { endRow: 9999, startRow: 0 },
  selectedSavedFilterId: undefined,
  sort: [{ field: activityFieldModel.getByName("startAt")!, order: SortOrder.ASC }],
  viewMode: CalendarViewMode.WEEK,
};

export interface CalendarState {
  activities: Activity[];
  loading?: boolean;
  mapEntities: MapEntityWithActivity[];
  nearbyEntities: MapEntry[];
  nearbyEntitiesLoading?: boolean;
  outOfCadenceEntities: OutOfCadenceEntity[];
  outOfCadenceEntitiesLoading?: boolean;
  postponeLoading?: boolean;
  searchResults: Activity[];
  searchResultsLoading?: boolean;
  suggestions: ActivitySuggestion[];
  toggleCompleteLoading?: boolean;
  totalFilteredRecords: number;
  totalRecords: number;
  viewState: CalendarViewState;
}

const initialState: CalendarState = {
  activities: [],
  loading: false,
  mapEntities: [],
  nearbyEntities: [],
  nearbyEntitiesLoading: false,
  outOfCadenceEntities: [],
  outOfCadenceEntitiesLoading: false,
  postponeLoading: false,
  searchResults: [],
  searchResultsLoading: false,
  suggestions: [],
  toggleCompleteLoading: false,
  totalFilteredRecords: 0,
  totalRecords: 0,
  viewState: DEFAULT_CALENDAR_VIEW_STATE,
};

const activityCalendar = createReducer<
  CalendarState,
  Actions | AppActions | CreateEditActivityModalActions
>(initialState)
  // TODO: change this to catch fetchCustomFields.success for activity entity type
  // to update view state every time custom fields are modified
  // load view settings after app initialization to ensure custom fields are fetched
  // OR not change, but add one more action
  .handleAction([initializeApp.success, reinitializeApp.success], (state) => {
    const viewState = preprocessViewStateForActivities(
      localSettings.getViewSettings(
        CALENDAR_VIEW_STATE,
        activityFieldModel,
        DEFAULT_CALENDAR_VIEW_STATE
      )
    );

    // Ensure that StartAt condition is present
    const startAtCondition = getSimpleConditionForField(
      viewState.filter,
      ActivityFieldName.START_AT,
      EntityType.ACTIVITY
    );
    if (!startAtCondition) {
      const defaultStartAtCondition = getSimpleConditionForField(
        DEFAULT_CALENDAR_VIEW_STATE.filter,
        ActivityFieldName.START_AT,
        EntityType.ACTIVITY
      )!;
      viewState.filter = upsertSimpleConditionsWithinTag(
        viewState.filter,
        EntityType.ACTIVITY,
        defaultStartAtCondition
      );
    }

    return {
      ...state,
      viewState: { ...state.viewState, ...DEFAULT_CALENDAR_VIEW_STATE, ...viewState },
    };
  })
  .handleAction(initializeCalendarView.success, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(applyCalendarViewSettings, (state, { payload }) => ({
    ...state,
    viewState: {
      ...state.viewState,
      filter: payload.filter ?? state.viewState.filter,
      range: payload.range ?? state.viewState.range,
      // only update selectedSavedFilterId when it is explicitly present in a payload (even when it is `undefined`)
      selectedSavedFilterId:
        "selectedSavedFilterId" in payload
          ? payload.selectedSavedFilterId
          : state.viewState.selectedSavedFilterId,
      sort: payload.sort ?? state.viewState.sort,
      viewMode: payload.viewMode ?? state.viewState.viewMode,
    },
  }))
  .handleAction(fetchCalendarActivities.request, (state, { payload: { updateOnly } }) => ({
    ...state,
    loading: !updateOnly,
  }))
  .handleAction(fetchCalendarActivities.success, (state, { payload }) => ({
    ...state,
    activities: payload.activities,
    loading: false,
    mapEntities: payload.mapEntities,
    suggestions: payload.suggestions,
    totalFilteredRecords: payload.totalFilteredRecords,
    totalRecords: payload.totalRecords,
  }))
  .handleAction(fetchCalendarActivities.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(updateActivity.success, (state, { payload: activity }) => {
    const activities = state.activities;
    const updatedActivityIndex = activities.findIndex(({ id }) => id === activity.id);
    if (updatedActivityIndex < 0) {
      return state;
    }
    return {
      ...state,
      activities: [
        ...activities.slice(0, updatedActivityIndex),
        activity,
        ...activities.slice(updatedActivityIndex + 1),
      ],
    };
  })
  .handleAction(updateActivityInList, (state, { payload }) => ({
    ...state,
    activities: state.activities.map((activity) =>
      activity.id === payload.id ? { ...activity, ...payload } : activity
    ),
    toggleCompleteLoading: false,
  }))
  .handleAction(postponeActivity.request, (state) => ({
    ...state,
    postponeLoading: true,
  }))
  .handleAction(postponeActivity.success, (state, { payload }) => ({
    ...state,
    activities: state.activities.map((activity) =>
      activity.id === payload.id ? { ...activity, ...payload } : activity
    ),
    postponeLoading: false,
  }))
  .handleAction(postponeActivity.failure, (state) => ({
    ...state,
    postponeLoading: false,
  }))
  .handleAction(moveResizeActivity.request, (state) => ({
    ...state,
    loading: true,
  }))
  .handleAction(moveResizeActivity.success, (state, { payload }) => ({
    ...state,
    activities: state.activities.map((activity) =>
      activity.id === payload.id ? { ...activity, ...payload } : activity
    ),
    loading: false,
  }))
  .handleAction(moveResizeActivity.failure, (state) => ({
    ...state,
    loading: false,
  }))
  .handleAction(fetchNearbyEntities.request, (state) => ({
    ...state,
    nearbyEntitiesLoading: true,
  }))
  .handleAction(fetchNearbyEntities.success, (state, { payload }) => ({
    ...state,
    nearbyEntities: payload,
    nearbyEntitiesLoading: false,
  }))
  .handleAction(fetchNearbyEntities.failure, (state) => ({
    ...state,
    nearbyEntitiesLoading: false,
  }))
  .handleAction(fetchOutOfCadenceEntities.request, (state) => ({
    ...state,
    outOfCadenceEntitiesLoading: true,
  }))
  .handleAction(fetchOutOfCadenceEntities.success, (state, { payload }) => ({
    ...state,
    outOfCadenceEntities: payload,
    outOfCadenceEntitiesLoading: false,
  }))
  .handleAction(fetchOutOfCadenceEntities.failure, (state) => ({
    ...state,
    outOfCadenceEntitiesLoading: false,
  }))
  .handleAction(findActivities.request, (state) => ({
    ...state,
    searchResultsLoading: true,
  }))
  .handleAction(findActivities.success, (state, { payload }) => ({
    ...state,
    searchResults: payload,
    searchResultsLoading: false,
  }))
  .handleAction(findActivities.failure, (state) => ({
    ...state,
    searchResultsLoading: false,
  }));

export * from "./selectors";
export type CalendarActions = Actions;
export default activityCalendar;
